// packages/shared/src/schemas/embed.ts
// Discord embed shape validator. Hoisted from the worker so the SPA and the
// worker validate the same JSON byte-identically (RESEARCH Pattern 4).
//
// Per-field limits cross-checked against
// https://docs.discord.com/developers/resources/message:
//   title         <= 256
//   description   <= 4096
//   field.name    <= 256          (1..256 — Discord rejects empty)
//   field.value   <= 1024         (1..1024)
//   footer.text   <= 2048
//   author.name   <= 256          (NOT used in Phase 3 form, schema omits it)
//   fields        <= 25 entries
//   color         0..0xFFFFFF integer
// Plus the global rule: the SUM of title + description + footer.text + each
// field.name + each field.value across all embeds in a message must be <= 6000.
//
// We surface the global violation at path ['_total'] so RHF can map it onto a
// top-level summary card while per-field issues stay at their own paths.

import { z } from 'zod';

// Discord accepts URL strings OR empty strings (latter for "field intentionally
// blank"). z.string().url() rejects empty by design — wrap in a union with
// z.literal('').
const URL_OR_EMPTY = z.union([z.string().url(), z.literal('')]);

const embedFieldSchema = z.object({
  name:   z.string().min(1).max(256),
  value:  z.string().min(1).max(1024),
  inline: z.boolean().optional(),
});

export const embedSchema = z
  .object({
    title:       z.string().max(256).optional(),
    description: z.string().max(4096).optional(),
    url:         URL_OR_EMPTY.optional(),
    color:       z.number().int().min(0).max(0xFFFFFF).optional(),
    footer: z
      .object({
        text:     z.string().max(2048),
        icon_url: URL_OR_EMPTY.optional(),
      })
      .optional(),
    thumbnail: z.object({ url: URL_OR_EMPTY }).optional(),
    image:     z.object({ url: URL_OR_EMPTY }).optional(),
    fields:    z.array(embedFieldSchema).max(25).optional(),
  })
  .superRefine((embed, ctx) => {
    // Discord 6,000-char total rule (RESEARCH Pitfall 1):
    let total = 0;
    total += embed.title?.length ?? 0;
    total += embed.description?.length ?? 0;
    total += embed.footer?.text.length ?? 0;
    for (const f of embed.fields ?? []) {
      total += f.name.length + f.value.length;
    }
    if (total > 6000) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Total embed text is ${total} characters; max 6000.`,
        path: ['_total'],
      });
    }
  });

export type Embed = z.infer<typeof embedSchema>;
